import React from 'react';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { Avatar } from '../Avatar';
import { BrandMark } from '../BrandMark';
import { HiveGrid } from '../engagement/HiveGrid';
import { FilterChip } from '../FilterChip';
import { communities } from '../../data/communities';
import { people } from '../../data/people';
import { useRecapConfig } from '../../hooks/recapConfigStore';
import { useLocale } from '../../contexts/LocaleContext';
import type { BeeArchetypeId, Recap } from '../../types/engagement';
import { hiveGridCells, hiveSnapshot, percentileEligible, rankedHabits, socialTotal, strongestHabits } from '../../utils/recap';

/** Shared story surface. Dark for celebratory moments, light for the hive and details. */
export function StoryCard({
  tone = 'light',
  overline,
  children
}: {
  tone?: 'light' | 'dark';
  overline?: string;
  children: React.ReactNode;
}) {
  const dark = tone === 'dark';
  return <article className={`flex min-h-[440px] flex-col rounded-panel p-3 ${dark ? 'bg-deep text-sand' : 'border border-sand-line bg-white/80 text-deep'}`} data-id="element-1296" data-mp="34e0htlspflt8">
      {overline && <p className={`bee-overline text-[9px] font-bold ${dark ? 'text-gold' : 'text-muted'}`} data-id="element-1297" data-mp="7g0d6ahgyj2x">{overline}</p>}
      <div className="flex flex-1 flex-col justify-center" data-id="element-1298" data-mp="bfiw50pqck34">{children}</div>
    </article>;
}
export function IntroCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t,
    tl
  } = useLocale();
  return <StoryCard tone="dark" data-id="element-1299" data-mp="l9jalt6wj1oi">
      <BrandMark size={40} data-id="element-1300" data-mp="q5qivsmrsp9m" />
      <p className="bee-overline mt-3 text-[9px] font-bold text-ondark" data-id="element-1301" data-mp="q4ymwgp9gpit">{tl(recap.periodLabel)}</p>
      <h2 className="mt-1 text-[30px] font-bold leading-tight text-sand" data-id="element-1302" data-mp="v6nzi0s4tb90">
        {recap.period === 'month' ? t('eng.recap.intro.month') : t('eng.recap.intro.year')}
      </h2>
      <p className="mt-2 max-w-[30ch] text-[14px] leading-relaxed text-ondark" data-id="element-1303" data-mp="2wx7w3kfhslkt">{t('eng.recap.intro.body')}</p>
    </StoryCard>;
}
export function BeeTypeCard({
  recap,
  archetype
}: {
  recap: Recap;
  archetype: BeeArchetypeId;
}) {
  const {
    t,
    tl
  } = useLocale();
  const content = useRecapConfig().archetypes[archetype];
  return <StoryCard tone="dark" overline={recap.period === 'year' ? t('eng.recap.bee.overlineYear') : t('eng.recap.bee.overline')} data-id="element-1304" data-mp="361bbxkz2kiq">
      <span className="grid h-16 w-16 place-items-center rounded-pill border border-gold/50" data-id="element-1305" data-mp="d2xfjr3u8khh">
        <BrandMark size={34} data-id="element-1306" data-mp="r2oym697or2c" />
      </span>
      <h2 className="mt-3 text-[28px] font-bold leading-tight text-gold" data-id="element-1307" data-mp="r1nbv4iu2g4s">{tl(content.name)}</h2>
      <p className="mt-2 text-[14px] leading-relaxed text-sand" data-id="element-1308" data-mp="m2khce0hbsb2">{tl(content.description)}</p>
      <p className="mt-3 border-t border-sand/15 pt-2 text-[11.5px] leading-snug text-ondark" data-id="element-1309" data-mp="10srq220opwyk">
        {t('eng.recap.bee.basis')} {t('eng.recap.bee.note')}
      </p>
    </StoryCard>;
}
export function HiveEvolutionCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t,
    tl
  } = useLocale();
  const start = hiveSnapshot(recap.hiveStartBee);
  const end = hiveSnapshot(recap.hiveEndBee);
  const total = hiveGridCells(end);
  const evolved = start.stage.id !== end.stage.id;
  const panes = [{
    label: t('eng.recap.hive.start'),
    snap: start
  }, {
    label: t('eng.recap.hive.end'),
    snap: end
  }];
  return <StoryCard overline={t('eng.recap.hive.overline')} data-id="element-1310" data-mp="1fe5jfxe0qqao">
      <p className="bee-num text-[26px] font-bold leading-tight text-deep" data-id="element-1311" data-mp="1fhjq3htptlnx">
        {t('eng.recap.hive.delta', {
        from: start.cells,
        to: end.cells
      })}
      </p>
      <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2" data-id="element-1312" data-mp="15om2ix9v96ig">
        {panes.map((pane, index) => <React.Fragment key={pane.label} data-id="element-1313" data-mp="1ahf81nd5sivl" data-mpi={index}>
            {index === 1 && <ArrowRightIcon size={18} className="text-gold-deep" aria-hidden="true" data-id="element-1314" data-mp="vs981x6hyomn" />}
            <figure className="min-w-0" data-id="element-1315" data-mp="10prftk8s2xt2">
              <HiveGrid filled={pane.snap.cells} total={total} columns={8} data-id="element-1316" data-mp="10qkia90pf15e" />
              <figcaption className="mt-1" data-id="element-1317" data-mp="mruker628xfz">
                <span className="block text-[11px] text-muted" data-id="element-1318" data-mp="msvyl4idi01x">{pane.label}</span>
                <span className="block text-[12.5px] font-bold text-deep" data-id="element-1319" data-mp="rotghf9abegt">{tl(pane.snap.stage.name)}</span>
                <span className="bee-num block text-[11.5px] text-muted" data-id="element-1320" data-mp="jr13ufqj1uz4">
                  {t('eng.recap.hive.cells', {
                n: pane.snap.cells
              })}
                </span>
              </figcaption>
            </figure>
          </React.Fragment>)}
      </div>
      <p className={`mt-3 flex items-start gap-2 rounded-card px-2 py-2 text-[13px] font-medium ${evolved ? 'border border-gold/60 bg-gold/15 text-deep' : 'bg-sand-deep/70 text-deep'}`} data-id="element-1321" data-mp="5wgu6p8ceju8">
        {evolved && <SparklesIcon size={16} className="mt-[1px] shrink-0 text-gold-deep" aria-hidden="true" data-id="element-1322" data-mp="3s6tb59z24oqy" />}
        {evolved ? t('eng.recap.hive.evolved', {
        from: tl(start.stage.name),
        to: tl(end.stage.name)
      }) : t('eng.recap.hive.same', {
        stage: tl(end.stage.name)
      })}
      </p>
    </StoryCard>;
}
export function ConsistencyCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t
  } = useLocale();
  const ratio = recap.checkDays / recap.daysInPeriod;
  return <StoryCard overline={t('eng.recap.consistency.overline')} data-id="element-1323" data-mp="2z9iowb20snuj">
      <p className="bee-num text-[64px] font-bold leading-none text-deep" data-id="element-1324" data-mp="349542q1ese5z">{recap.checkDays}</p>
      <p className="mt-1 text-[14px] text-deep" data-id="element-1325" data-mp="390dd3kyttxim">
        {t('eng.recap.consistency.days', {
        n: recap.checkDays,
        total: recap.daysInPeriod
      })}
      </p>
      <div className="mt-2 h-[6px] overflow-hidden rounded-pill bg-sand-deep" aria-hidden="true" data-id="element-1326" data-mp="3d172yjvtxme5">
        <div className="h-full rounded-pill bg-gold" style={{
        width: `${Math.min(1, ratio) * 100}%`
      }} data-id="element-1327" data-mp="2gd0q59or7nos" />
      </div>
      <ul className="mt-3 space-y-1 text-[13px] text-muted" data-id="element-1328" data-mp="2lcn2zi0o71x2">
        <li className="bee-num" data-id="element-1329" data-mp="2pb2v20blz0hc">{t('eng.recap.consistency.streak', {
          n: recap.longestStreak
        })}</li>
        <li className="bee-num" data-id="element-1330" data-mp="2uca2tzdr3zo0">{t('eng.recap.consistency.avg', {
          n: recap.averageLifeScore
        })}</li>
      </ul>
    </StoryCard>;
}
export function HabitCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t
  } = useLocale();
  const top = strongestHabits(recap);
  const ranked = rankedHabits(recap);
  const name = (id: string) => t(`habit.${id}.title`);
  return <StoryCard overline={t('eng.recap.habit.overline')} data-id="element-1331" data-mp="21mjdg6k2vp4o">
      <h2 className="text-[26px] font-bold leading-tight text-deep" data-id="element-1332" data-mp="21lzvipufh0qn">
        {top.length > 1 ? t('eng.recap.habit.tied', {
        a: name(top[0]),
        b: name(top[1])
      }) : name(top[0])}
      </h2>
      <p className="bee-num mt-1 text-[13px] text-muted" data-id="element-1333" data-mp="2aoix76mh97bb">
        {t('eng.recap.habit.ratio', {
        n: Math.round(ranked[0].ratio * 100)
      })}
      </p>
      <ul className="mt-3 space-y-2" data-id="element-1334" data-mp="1mval3posjpnu">
        {ranked.map(item => <li key={item.habit} data-id="element-1335" data-mp="1i0dcgjvfbibh">
            <span className="flex justify-between text-[12px] text-deep" data-id="element-1336" data-mp="1wng96obx9mxr">
              {name(item.habit)}
              <span className="bee-num text-muted" data-id="element-1337" data-mp="1rwztexkmpp88">{Math.round(item.ratio * 100)}%</span>
            </span>
            <span className="mt-[3px] block h-[4px] overflow-hidden rounded-pill bg-sand-deep" aria-hidden="true" data-id="element-1338" data-mp="146lzf1ihhgb0">
              <span className={`block h-full rounded-pill ${top.includes(item.habit) ? 'bg-gold' : 'bg-soft'}`} style={{
            width: `${item.ratio * 100}%`
          }} data-id="element-1339" data-mp="z7118tb51bzc" />
            </span>
          </li>)}
      </ul>
    </StoryCard>;
}
export function ActivityCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t,
    tl
  } = useLocale();
  return <StoryCard overline={t('eng.recap.activity.overline')} data-id="element-1340" data-mp="35vta1udb1w8x">
      <h2 className="text-[34px] font-bold leading-tight text-deep" data-id="element-1341" data-mp="35u8fw5uwkfbl">{tl(recap.mainActivity)}</h2>
      <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-1342" data-mp="3tjsz8puyxele">{t('eng.recap.activity.body')}</p>
    </StoryCard>;
}
export function CommunityCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t
  } = useLocale();
  return <StoryCard overline={t('eng.recap.community.overline')} data-id="element-1343" data-mp="3pkafhxevv9k5">
      <p className="bee-num text-[34px] font-bold leading-tight text-deep" data-id="element-1344" data-mp="3piqyu5pbewhu">
        {t('eng.recap.community.posts', {
        n: recap.communityPosts
      })}
      </p>
      <p className="bee-num mt-1 text-[18px] font-medium text-deep" data-id="element-1345" data-mp="2e5ohv9jb04x5">
        {t('eng.recap.community.challenges', {
        n: recap.challengesDone
      })}
      </p>
    </StoryCard>;
}
export function SocialCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t
  } = useLocale();
  const person = people.find(item => item.id === recap.socialHighlight?.personId);
  if (!person) return null;
  const first = person.name.split(' ')[0];
  return <StoryCard tone="dark" overline={t('eng.recap.social.overline')} data-id="element-1346" data-mp="2qwh72v2vjebj">
      <Avatar src={person.avatar} name={person.name} size={72} data-id="element-1347" data-mp="3ojpp2g3o258s" />
      <h2 className="mt-3 text-[24px] font-bold leading-tight text-sand" data-id="element-1348" data-mp="3olaho06f7e40">
        {recap.period === 'month' ? t('eng.recap.social.month', {
        name: first
      }) : t('eng.recap.social.year', {
        name: first
      })}
      </h2>
      <p className="bee-num mt-1 text-[14px] text-gold" data-id="element-1349" data-mp="39utkit0tjacx">{t('eng.recap.social.count', {
        n: socialTotal(recap)
      })}</p>
      <p className="mt-3 text-[11.5px] text-ondark" data-id="element-1350" data-mp="b4l3vqm8teqi">{t('eng.recap.social.note')}</p>
    </StoryCard>;
}
export function PercentileCard({
  recap,
  communityId,
  onChoose
}: {
  recap: Recap;
  communityId: string;
  onChoose: (id: string) => void;
}) {
  const {
    t,
    tl
  } = useLocale();
  const context = recap.communityContext.find(item => item.communityId === communityId);
  const community = communities.find(item => item.id === communityId);
  const name = community ? tl(community.name) : communityId;
  const eligible = percentileEligible(context);
  return <StoryCard overline={t('eng.recap.pct.overline')} data-id="element-1351" data-mp="wnliow779lhy">
      {eligible && context ? <>
          <p className="bee-num text-[56px] font-bold leading-none text-deep" data-id="element-1352" data-mp="11momkdbg1loc">{context.topPercent}%</p>
          <p className="mt-2 text-[15px] leading-snug text-deep" data-id="element-1353" data-mp="16domlrfv0jv2">
            {recap.period === 'month' ? t('eng.recap.pct.month', {
          n: context.topPercent,
          community: name
        }) : t('eng.recap.pct.year', {
          n: context.topPercent,
          community: name
        })}
          </p>
        </> : <p className="text-[15px] leading-snug text-deep" data-id="element-1354" data-mp="1kcocf2f3z4st">{t('eng.recap.pct.insufficient')}</p>}
      {recap.communityContext.length > 1 && <div className="mt-3" data-id="element-1355" data-mp="7v246igimxaw">
          <p className="text-[11.5px] font-medium text-muted" data-id="element-1356" data-mp="csbus1fn5f0b">{t('eng.recap.pct.choose')}</p>
          <div className="mt-1 flex flex-wrap gap-1" data-id="element-1357" data-mp="hrexhf85f954">
            {recap.communityContext.map(item => {
          const c = communities.find(entry => entry.id === item.communityId);
          return <FilterChip key={item.communityId} label={c ? tl(c.name) : item.communityId} active={item.communityId === communityId} onClick={() => onChoose(item.communityId)} data-id="element-1358" data-mp="rhrg31weks8m" />;
        })}
          </div>
        </div>}
      <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-1359" data-mp="15fofs765leri">{t('eng.recap.pct.note')}</p>
    </StoryCard>;
}
export function MilestonesCard({
  recap
}: {
  recap: Recap;
}) {
  const {
    t,
    tl
  } = useLocale();
  return <StoryCard tone="dark" overline={t('eng.recap.milestones.overline')} data-id="element-1360" data-mp="378megjmm31kd">
      <ul className="space-y-3" data-id="element-1361" data-mp="2zpwj3w0mx742">
        {recap.highlights.map(item => <li key={tl(item.label)} className="border-b border-sand/15 pb-2 last:border-b-0" data-id="element-1362" data-mp="2uxldqdn4kt44">
            <span className="block text-[11.5px] text-ondark" data-id="element-1363" data-mp="2pyifbc1nhl54">{tl(item.label)}</span>
            <span className="block text-[17px] font-bold text-sand" data-id="element-1364" data-mp="2kzyq92rrft8j">{tl(item.value)}</span>
          </li>)}
      </ul>
    </StoryCard>;
}